// § 35a (#564 S5c): Vorschlag holen, prüfen, übernehmen – je Rechnung (Sidebar) und gesammelt je WEG und Jahr (Objekt › Finanzen).
import { useCallback, useEffect, useState } from 'react';
import { ArrowCounterClockwise, Check, MagicWand, Prohibit, Receipt } from '@phosphor-icons/react';
import { Chip, ImmoButton, InnenKarte, KartenTitel, SegmentedControl, TextFeld, ToastPill, useToastPill } from '../immobilien/ui';
import { eur } from '../../lib/immobilien/format';
import {
  ART_TEXT, holeVorschlag35a, ladeRechnung35a, ladeRechnungen35a, ladeSchalter35a, ladeVorschlaege35a, setze35aZurueck, uebernehme35a, verwerfe35a,
  type Rechnung35a, type Vorschlag35a,
} from '../../lib/buchhaltung/paragraph35a';

const leise = { fontSize: 12, color: 'var(--ink3)' } as const;
const zahlText = (n: number | null) => (n == null ? '' : n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const leseZahl = (s: string) => { const t = s.trim(); if (!t) return null; const v = Number(t.replace(/\./g, '').replace(',', '.')); return Number.isFinite(v) ? v : null; };

export function Zeile35a({ r, v, aktiv, onGeaendert, zeige }: {
  r: Rechnung35a; v: Vorschlag35a | undefined; aktiv: boolean; onGeaendert: () => void; zeige: (t: string, f?: boolean) => void;
}) {
  const vorschlagBetrag = v && v.status === 'vorschlag' ? (v.lohn ?? 0) + (v.fahrt ?? 0) : null;
  const [betrag, setBetrag] = useState(zahlText(vorschlagBetrag));
  const [laeuft, setLaeuft] = useState(false);
  const vorschlagArt = v?.art === 'handwerker' || v?.art === 'haushaltsnah' ? v.art : null;
  const [art, setArt] = useState<'handwerker' | 'haushaltsnah' | null>(vorschlagArt);
  useEffect(() => { setBetrag(zahlText(vorschlagBetrag)); }, [vorschlagBetrag]);
  useEffect(() => { setArt(vorschlagArt); }, [vorschlagArt]);
  const tu = async (f: () => Promise<void>, ok: string) => {
    setLaeuft(true);
    try { await f(); zeige(ok); onGeaendert(); } catch (e) { zeige((e as Error).message, true); } finally { setLaeuft(false); }
  };
  const entschieden = r.lohnanteil_35a != null;
  const b = leseZahl(betrag);
  return (
    <div className="immo-innen flex flex-col" style={{ borderRadius: 16, padding: 12, gap: 8 }}>
      <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
        <span style={{ flex: '1 1 200px', minWidth: 0, fontSize: 13, fontWeight: 700 }}>{r.unternehmen || '–'} <span style={{ fontWeight: 400, color: 'var(--ink3)' }}>· {r.bezeichnung || r.rechnungsnummer || ''}</span></span>
        <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{r.brutto != null ? eur(r.brutto) : '–'}</span>
        {entschieden
          ? <Chip ton={r.lohnanteil_35a ? 'ok' : 'neutral'}>{r.lohnanteil_35a ? `${v?.art === 'haushaltsnah' ? 'Haushaltsnah' : v?.art === 'handwerker' ? 'Handwerker' : '§35a'} ${eur(r.lohnanteil_35a)}` : 'nicht begünstigt'}</Chip>
          : v?.status === 'vorschlag' ? <Chip ton="info">Vorschlag</Chip> : <Chip>offen</Chip>}
      </div>
      {!entschieden && v?.status === 'vorschlag' && (
        <div className="flex flex-col" style={{ gap: 4, fontSize: 12.5 }}>
          <span><b>{ART_TEXT[v.art]}</b>{v.lohn != null ? ` · Lohn ${eur(v.lohn)}` : ''}{v.fahrt != null ? ` · Fahrt ${eur(v.fahrt)}` : ''}{v.material != null ? ` · Material ${eur(v.material)}` : ''}</span>
          {v.fundstelle && <span style={{ ...leise, fontStyle: 'italic' }}>„{v.fundstelle}“</span>}
          {v.begruendung && <span style={leise}>{v.begruendung}</span>}
        </div>
      )}
      <div className="flex flex-wrap items-end" style={{ gap: 8 }}>
        {!entschieden && (
          <>
            <ImmoButton art="glas" groesse={32} icon={MagicWand} disabled={!aktiv || !r.dokument_id || laeuft}
              title={!aktiv ? 'Im Control Center ausgeschaltet' : !r.dokument_id ? 'Kein PDF zur Rechnung' : 'KI liest den Lohnanteil aus dem PDF'}
              onClick={() => void tu(() => holeVorschlag35a(r), 'Vorschlag geholt')}>{laeuft ? 'Liest …' : v ? 'Neu vorschlagen' : 'Vorschlag holen'}</ImmoButton>
            <div style={{ width: 150 }}><TextFeld label="Lohn inkl. Fahrt" inputMode="decimal" suffix="€" wert={betrag} onChange={setBetrag} placeholder="0,00" kompakt /></div>
            <SegmentedControl ariaLabel="Art nach § 35a" hoehe={30} wert={art ?? 'keine'} onChange={(k) => setArt(k === 'keine' ? null : k)}
              segmente={[{ key: 'handwerker', label: 'Handwerker' }, { key: 'haushaltsnah', label: 'Haushaltsnah' }, ...(art ? [] : [{ key: 'keine' as const, label: 'Art?' }])]} />
            <ImmoButton art="akzent" groesse={32} icon={Check} disabled={laeuft || !art || b == null || b < 0 || (r.brutto != null && b > r.brutto * 1.01)}
              title={!art ? 'Bitte Handwerker oder Haushaltsnah wählen' : undefined}
              onClick={() => void tu(() => uebernehme35a(r.id, b!, art!), 'Lohnanteil übernommen')}>Übernehmen</ImmoButton>
            <ImmoButton art="glas" groesse={32} icon={Prohibit} disabled={laeuft} onClick={() => void tu(() => verwerfe35a(r.id), 'Als nicht begünstigt markiert')}>Nicht begünstigt</ImmoButton>
          </>
        )}
        {entschieden && <ImmoButton art="glas" groesse={32} icon={ArrowCounterClockwise} disabled={laeuft} onClick={() => void tu(() => setze35aZurueck(r.id), 'Zurückgesetzt')}>Zurücksetzen</ImmoButton>}
      </div>
    </div>
  );
}

/** Objekt › Finanzen (WEG): alle Rechnungen eines Jahres prüfen. */
export function Paragraph35aKarte({ objektId, jahr, zeige }: { objektId: string; jahr: number; zeige: (t: string, f?: boolean) => void }) {
  const [rechnungen, setRechnungen] = useState<Rechnung35a[] | null>(null);
  const [vorschlaege, setVorschlaege] = useState<Map<string, Vorschlag35a>>(new Map());
  const [aktiv, setAktiv] = useState(false);
  const [alle, setAlle] = useState<{ fertig: number; gesamt: number } | null>(null);
  const lade = useCallback(async () => {
    try {
      const [rs, a] = await Promise.all([ladeRechnungen35a(objektId, jahr), ladeSchalter35a()]);
      setRechnungen(rs); setAktiv(a); setVorschlaege(await ladeVorschlaege35a(rs.map((r) => r.id)));
    } catch (e) { zeige(`§35a nicht geladen: ${(e as Error).message}`, true); setRechnungen([]); }
  }, [objektId, jahr, zeige]);
  useEffect(() => { void lade(); }, [lade]);

  const offen = (rechnungen ?? []).filter((r) => r.lohnanteil_35a == null && r.dokument_id && !vorschlaege.has(r.id));
  const summe = (rechnungen ?? []).reduce((s, r) => s + (r.lohnanteil_35a ?? 0), 0);
  const holeAlle = async () => {
    setAlle({ fertig: 0, gesamt: offen.length }); let fehler = 0;
    for (const [i, r] of offen.entries()) {
      try { await holeVorschlag35a(r); } catch { fehler++; }
      setAlle({ fertig: i + 1, gesamt: offen.length });
    }
    setAlle(null); zeige(fehler ? `${offen.length - fehler} Vorschläge geholt, ${fehler} ohne Ergebnis` : `${offen.length} Vorschläge geholt`, fehler > 0);
    await lade();
  };
  return (
    <InnenKarte>
      <KartenTitel icon={Receipt} rechts={`bestätigt ${eur(summe)}`}>§ 35a – Lohnanteile {jahr}</KartenTitel>
      <span style={leise}>
        Für die § 35a-Bescheinigung der selbst nutzenden Eigentümer. Die KI liest Lohn-, Fahrt- und Materialanteil aus dem PDF und schlägt nur vor;
        erst „Übernehmen“ trägt den Betrag in die Rechnung ein. {aktiv ? '' : 'Die KI-Auslesung ist im Control Center ausgeschaltet – Beträge lassen sich trotzdem von Hand eintragen.'}
      </span>
      {aktiv && offen.length > 0 && (
        <div><ImmoButton art="glas" icon={MagicWand} disabled={!!alle} onClick={() => void holeAlle()}>
          {alle ? `Liest ${alle.fertig} / ${alle.gesamt} …` : `Für ${offen.length} offene Rechnungen Vorschläge holen`}
        </ImmoButton></div>
      )}
      {rechnungen === null && <span style={leise}>Lädt …</span>}
      {rechnungen?.length === 0 && <span style={leise}>Keine Rechnungen zu diesem Objekt in {jahr}.</span>}
      {rechnungen?.map((r) => <Zeile35a key={r.id} r={r} v={vorschlaege.get(r.id)} aktiv={aktiv} onGeaendert={() => void lade()} zeige={zeige} />)}
    </InnenKarte>
  );
}

/** Rechnungs-Sidebar: eine Rechnung (lädt Zeile, Vorschlag und Schalter selbst). */
export function Rechnung35aBox({ rechnungId }: { rechnungId: string }) {
  const [r, setR] = useState<Rechnung35a | null>(null);
  const [v, setV] = useState<Vorschlag35a | undefined>(undefined);
  const [aktiv, setAktiv] = useState(false);
  const [toast, zeige] = useToastPill();
  const lade = useCallback(async () => {
    const [zeile, m, a] = await Promise.all([ladeRechnung35a(rechnungId), ladeVorschlaege35a([rechnungId]), ladeSchalter35a()]);
    setR(zeile); setV(m.get(rechnungId)); setAktiv(a);
  }, [rechnungId]);
  useEffect(() => { void lade().catch(() => setR(null)); }, [lade]);
  if (!r) return null;
  return <>{toast && <ToastPill text={toast.text} fehler={toast.fehler} />}<Zeile35a r={r} v={v} aktiv={aktiv} onGeaendert={() => void lade()} zeige={zeige} /></>;
}
